import Model from "./Model";
import AppPermission from "./AppPermission.ts";
import {useCookies} from "../composables/useCookies.ts";
import User from "./User.ts";
import api from "../api.ts";

/**
 * Represents a role in the application.
 * A role includes an ID, title, and associated permissions.
 *
 * @author Jackson Bingham <jackson@onlineimage.com>
 * @date November 2024
 */
class AppRole extends Model {
    /**
     * The title of the role.
     * @private
     */
    private _title: string;

    /**
     * The list of permissions associated with the role.
     * @private
     */
    private _permissions: AppPermission[];

    /**
     * The list of users assigned to this role.
     * @private
     */
    private _users?: User[] = [];

    /**
     * Creates a new role by sending a POST request to the server.
     *
     * This method sends the provided role data to the server, associates it with the specified client ID,
     * and returns the newly created role along with its success status.
     *
     * @param client_id - The client ID of the application the role will belong to.
     * @param values - The form data to create a new role, including its title and associated permission IDs.
     * @returns A Promise that resolves to a tuple:
     * - The first element is a boolean indicating whether the creation was successful (`true`) or not (`false`).
     * - The second element is the newly created `AppRole` instance, or `null` if the creation failed.
     * - The third element is an `Error` object if the creation failed, or `undefined` if it succeeded.
     *
     * @example
     * ```ts
     * const [success, newRole, error] = await AppRole.create("client123", {
     *   title: "Manager",
     *   permission_ids: ["101", "102"],
     * });
     *
     * if (success) {
     *   console.log(newRole.title); // "Manager"
     * } else {
     *   console.error(error.message);
     * }
     * ```
     */
    public static async create(client_id: string, values: IRoleForm): Promise<[boolean, AppRole, Error?]> {
        const tokenCookie = useCookies().getCookie("access_token");

        try {
            const response = await api.post(
                `/api/oix/roles/roles`,
                values, // Axios automatically stringifies JSON objects
                {
                    withCredentials: true,
                    headers: {
                        Authorization: `Bearer ${tokenCookie}`,
                    },
                    params: {
                        client_id,
                    }
                }
            );
            const role: AppRole = AppRole.fromJson(response.data);
            return [true, role];
        } catch (error) {
            return [false, null, error];
        }
    }

    /**
     * Retrieves all roles from the server.
     *
     * @param client_id The client id of the application that the role belongs to.
     * @returns A Promise that resolves to a list of `AppRole` instances.
     * @example
     * ```ts
     * const roles = await AppRole.getAll();
     * console.log(roles);
     * ```
     */
    public static async getAll(client_id: string): Promise<AppRole[]> {
        const tokenCookie = useCookies().getCookie("access_token");

        const response = await api.get(`/api/oix/roles/roles`, {
            withCredentials: true,
            headers: {
                Authorization: `Bearer ${tokenCookie}`,
            },
            params: {
                client_id, // Axios handles query string construction
            },
        });

        return response.data.roles.map((role: any) => AppRole.fromJson(role));
    }

    /**
     * Creates a new `AppRole` instance from a JSON object.
     *
     * @param json - The JSON object representing the role.
     * @returns A new `AppRole` instance.
     * @example
     * const json = { id: "1", title: "Editor", permissions: [...] };
     * const role = AppRole.fromJson(json);
     * console.log(role.title); // "Editor"
     */
    public static fromJson(json: any): AppRole {
        return new AppRole(json.id, json.title, json.permissions, json.users);
    }

    /**
     * Constructs a new AppRole instance.
     *
     * @param id - The unique identifier for the role.
     * @param title - The title of the role.
     * @param permissions - An array of permissions associated with the role.
     * @param users - An array of the users assigned this role.
     * @example
     * const role = new AppRole("1", "Admin", [
     *   { id: "101", title: "create_account", category: { id: "201", title: "Accounts" } },
     *   { id: "102", title: "delete_account", category: { id: "201", title: "Accounts" } },
     * ]);
     * console.log(role.permissions.length); // 2
     */
    constructor(id: string, title: string, permissions: any[], users: any[]) {
        super(id);
        this._title = title;
        this._permissions = permissions?.map((permission) =>
            AppPermission.fromJson(permission)
        );
        this._users = users?.map((user) =>
            User.fromJson(user)
        );
    }

    /**
     * Gets the title of the role.
     *
     * @returns The title of the role.
     * @example
     * ```ts
     * const role = new AppRole("1", "Admin", []);
     * console.log(role.title); // "Admin"
     * ```
     */
    public get title(): string {
        return this._title;
    }

    /**
     * Gets the list of permissions associated with the role.
     *
     * @returns An array of `AppPermission` objects.
     * @example
     * ```ts
     * const role = new AppRole("1", "Admin", [
     *   { id: "101", title: "create_account", category: { id: "201", title: "Accounts" } },
     * ]);
     * console.log(role.permissions[0].title); // "create_account"
     * ```
     */
    public get permissions(): AppPermission[] {
        return this._permissions;
    }

    /**
     * Gets the list of unique permission IDs associated with the role.
     *
     * @returns An array of unique permission IDs as strings.
     * @example
     * ```ts
     * const role = new AppRole("1", "Admin", [
     *   { id: "101", title: "create_account", category: { id: "201", title: "Accounts" } },
     *   { id: "102", title: "delete_account", category: { id: "201", title: "Accounts" } },
     *   { id: "101", title: "create_account", category: { id: "201", title: "Accounts" } },
     * ]);
     * console.log(role.permission_ids); // ["101", "102"]
     * ```
     */
    public get permission_ids(): string[] {
        const ids = this._permissions.map(permission => permission.id);
        return [...new Set(ids)]; // Removes duplicates by converting to a Set and back to an array
    }

    /**
     * Gets the list of users associated with the role.
     *
     * @returns An array of `User` objects representing the users associated with the role.
     * @example
     * ```ts
     * const role = new AppRole("1", "Admin", [], [new User("1", "John Doe"), new User("2", "Jane Smith")]);
     * console.log(role.users.length); // 2
     * console.log(role.users[0].name); // "John Doe"
     * ```
     */
    public get users(): User[] {
        return this._users;
    }

    /**
     * Converts the `AppRole` instance to a plain JSON object.
     *
     * This method overrides the base `toJSON` implementation to include the `title`, `permissions`, and `users` fields.
     *
     * @override
     * @returns An object containing the `AppRole`'s `id`, `title`, associated permissions, and users.
     *
     * @example
     * ```ts
     * const role = new AppRole("123", "Admin", [{ id: "101", title: "create_account" }], [new User()]);
     * console.log(role.toJSON());
     * // { id: "123", title: "Admin", permissions: [...], users: [...] }
     * ```
     */
    public override toJSON() {
        return {
            id: this.id,
            title: this._title,
            permissions: this._permissions,
            users: this._users,
        }
    }

    /**
     * Updates the current role by sending a PATCH request to the server.
     *
     * This method sends the updated role data along with the client ID to the server,
     * updates the current instance with the response, and returns the updated role along with its success status.
     *
     * @param client_id - The client ID of the application the role belongs to.
     * @param values - The form data to update the role, including its updated title and permission IDs.
     * @returns A Promise that resolves to a tuple:
     * - The first element is a boolean indicating whether the update was successful (`true`) or not (`false`).
     * - The second element is the updated `AppRole` instance, or `null` if the update failed.
     * - The third element is an `Error` object if the update failed, or `undefined` if it succeeded.
     *
     * @example
     * ```ts
     * const role = new AppRole("1", "Admin", []);
     * const [success, updatedRole, error] = await role.update("client123", { title: "Super Admin" });
     *
     * if (success) {
     *   console.log(updatedRole.title); // "Super Admin"
     * } else {
     *   console.error(error.message);
     * }
     * ```
     */
    public async update(client_id: string, values: IRoleForm): Promise<[boolean, AppRole, Error?]> {
        const tokenCookie = useCookies().getCookie("access_token");

        try {
            const response = await api.patch(
                `/api/oix/roles/roles/${this.id}`,
                values,
                {
                    withCredentials: true,
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${tokenCookie}`,
                    },
                    params: {
                        client_id,
                    }
                }
            );

            // Update local instance with the response data
            this._title = response.data.role.title;
            this._permissions = response.data.role.permissions.map((permission: object) =>
                AppPermission.fromJson(permission)
            );
            this._users = response.data.role.users?.map((user: User) =>
                User.fromJson(user)
            );
            return [true, this];
        } catch (error) {
            console.error(error);
            return [false, null, error];
        }
    }

    /**
     * Deletes the current role by sending a DELETE request to the server.
     *
     * @param client_id - The client ID of the application the role belongs to.
     * @returns A Promise that resolves to a tuple:
     * - The first element is a boolean indicating whether the deletion was successful (`true`) or not (`false`).
     * - The second element is an `Error` object if the deletion failed, or `undefined` if it succeeded.
     *
     * @example
     * ```ts
     * const role = new AppRole("1", "Admin", []);
     * const [success, error] = await role.destroy("client123");
     *
     * if (success) {
     *   console.log("Role successfully deleted");
     * } else {
     *   console.error(error.message);
     * }
     * ```
     */
    public async destroy(client_id: string): Promise<[boolean, Error?]> {
        const tokenCookie = useCookies().getCookie("access_token");

        try {
            // Send DELETE request to the server
            await api.delete(`/api/oix/roles/roles/${this.id}`, {
                withCredentials: true,
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${tokenCookie}`,
                },
                params: {
                    client_id,
                },
            });

            return [true];
        } catch (error) {
            console.error("Error deleting role:", error);
            return [false, error];
        }
    }
}

export default AppRole;

/**
 * Interface representing the structure of the form used to create or update a role.
 */
export interface IRoleForm {
    /** The title of the role. */
    title: string;

    /** A list of users associated with the role. */
    users?: User[];

    /** A list of permission IDs associated with the role. */
    permission_ids?: string[];
}